// LOTO, Stage 5 piece 3. Words: LOTO_WORDS.md rev 2, mockup MOCKUP_LOTO.html (approved by Robert 2026-10-11).
//
// One email when the JSP opens (Submit). Every JSP. The LOTO line rides in it:
// "✓ Locked out" on a de-energized job, "Not required" on an energized one.
// To the QEW and the site contacts with the LOTO Awareness tick. Nothing at close.
// Practice JSPs on demo assets send nothing.
//
// Every email names the QEW by name (Robert, 2026-10-11): subject, heading and QEW row.
// Pure: data in, data out. Unit tested.

import { siteContactsFor, siteNameOf, type AlertWorld, type PlannedAlert } from "./alerts";
import type { EmailRequest, Recipient } from "./email";

export const WORK_STARTED_REASON = "Work started";

export interface WorkStartedJob {
  jspNumber: string;
  siteNumber: string;
  qewId: string;
  qewName: string;
  qewEmail: string;
  taskName: string;
  equipment: string;
  location: string;
  assetId: string;
  /** "10/11 at 8:14 AM", site time. */
  startedText: string;
  isDeEnergized: boolean;
  /** "1", "2", "3" or "4+". */
  sourceCount: string;
  otherEnergy: string[];
  isWorkingAlone: boolean;
  /** The work request this JSP wrote, as the Findings row (WORK_REQUEST_WORDS.md section 5). */
  findings?: string;
  /** EEWP_WORDS.md rev 3: "EEWP-1004 · Approved by Pat Lee, Chris Doyle". */
  eewp?: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The LOTO row. */
export function lotoLineOf(j: Pick<WorkStartedJob, "isDeEnergized" | "sourceCount" | "otherEnergy">): string {
  if (!j.isDeEnergized) return "Not required. Energized task, PPE and boundary in place.";
  const n = j.sourceCount;
  const src = n ? `${n} electrical source${n === "1" ? "" : "s"}` : "";
  return ["✓ Locked out", src, ...j.otherEnergy].filter(Boolean).join(" · ");
}

/** "10/11 at 8:14 AM" in site time. */
export function startedTextOf(iso: string, timeZone = "America/New_York"): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = new Intl.DateTimeFormat("en-US", { timeZone, month: "2-digit", day: "2-digit" }).format(d);
  const time = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" }).format(d);
  return `${day} at ${time}`;
}

/** "#0082" from "JSP-0082". */
export function jspShort(jspNumber: string): string {
  return `#${jspNumber.replace(/^JSP-/, "")}`;
}

/** Null when nobody has an email. */
export function workStartedAlert(j: WorkStartedJob, world: AlertWorld): PlannedAlert | null {
  const site = siteNameOf(world, j.siteNumber);
  const num = jspShort(j.jspNumber);
  const name = j.qewName.trim() || j.qewId;
  const base: Omit<EmailRequest, "to" | "toLabel" | "footer"> = {
    siteNumber: j.siteNumber,
    siteName: site,
    companyName: world.companyName,
    subject: `Work Started · ${name} · ${j.equipment || j.assetId} · JSP ${num}`,
    headline: `${name} is working on electrical equipment.`,
    lines: [`Started at ${site}. The JSP is open.`, ...(j.isWorkingAlone ? ["Working alone. Check in if you do not hear back."] : [])],
    rows: [
      { label: "LOTO", value: lotoLineOf(j) },
      ...(j.eewp ? [{ label: "EEWP", value: j.eewp }] : []),
      ...(j.findings ? [{ label: "Findings", value: j.findings }] : []),
      { label: "QEW", value: `${name} · ${j.qewId}` },
      { label: "Task", value: j.taskName },
      { label: "Equipment", value: j.equipment || "Not on record" },
      { label: "Location", value: j.location || "Not on record" },
      { label: "Asset ID", value: j.assetId },
      { label: "Started", value: j.startedText },
      { label: "JSP", value: num },
    ],
    button: null,
    reason: WORK_STARTED_REASON,
    recordId: j.jspNumber,
  };
  const sign = world.companyName ? `Sent by SafeTag for the ${world.companyName} Electrical Safety Program.` : "Sent by SafeTag for the Electrical Safety Program.";
  const qew: Recipient[] = EMAIL.test(j.qewEmail.trim()) ? [{ name, email: j.qewEmail.trim() }] : [];
  const seen = new Set(qew.map((r) => r.email.toLowerCase()));
  const contacts = siteContactsFor(world, j.siteNumber, "lotoAwareness").filter((r) => !seen.has(r.email.toLowerCase()));
  const emails: EmailRequest[] = [];
  if (qew.length) emails.push({ ...base, to: qew, toLabel: name, footer: `You get this because you submitted this JSP. ${sign}` });
  if (contacts.length)
    emails.push({
      ...base,
      to: contacts,
      toLabel: `${site} LOTO Awareness contacts`,
      footer: `You get this as a LOTO Awareness contact at ${site}. One email per JSP, when the work starts. ${sign}`,
    });
  return emails.length ? { key: `work-started-${j.jspNumber}`, emails } : null;
}
